import type { MessageKey } from '@dijitalapp/i18n';
import type { CapabilitiesDto, ExperienceDto, NavItem, SpaceDto } from '@dijitalapp/shared';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { Icon } from '../components/Icon';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { ConversationList } from './Messages';

interface ShellState {
  spaces: SpaceDto[];
  activeSpace: SpaceDto | null;
  experience: ExperienceDto | null;
  capabilities: CapabilitiesDto['capabilities'] | null;
  selectSpace: (id: string) => void;
  reload: () => Promise<void>;
  setMobileView: (v: 'list' | 'content') => void;
}

const ShellCtx = createContext<ShellState | null>(null);
export function useShell(): ShellState {
  const v = useContext(ShellCtx);
  if (!v) throw new Error('useShell outside Shell');
  return v;
}

interface ShellData {
  spaces: SpaceDto[];
  capabilities: CapabilitiesDto['capabilities'];
}

async function loadShellData(): Promise<ShellData> {
  const [{ spaces }, caps] = await Promise.all([
    api<{ spaces: SpaceDto[] }>('GET', '/spaces'),
    api<CapabilitiesDto>('GET', '/me/capabilities'),
  ]);
  return { spaces, capabilities: caps.capabilities };
}

const NAV_LABELS: Record<NavItem, MessageKey> = {
  messages: 'nav.messages',
  calls: 'nav.calls',
  spaces: 'nav.spaces',
  discover: 'nav.discover',
  ai: 'nav.ai',
};

export function Shell() {
  const { i18n, me, online, config } = useApp();
  const { t } = i18n;
  const location = useLocation();
  const [spaces, setSpaces] = useState<SpaceDto[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [experience, setExperience] = useState<ExperienceDto | null>(null);
  const [capabilities, setCapabilities] = useState<CapabilitiesDto['capabilities'] | null>(null);
  // Pathname at which the user stepped back to the list on mobile; any navigation shows content.
  const [listAt, setListAt] = useState<string | null>(null);
  // Root and the chat home show the list on phones; everything else shows its content pane.
  const mobileView =
    location.pathname === '/' || location.pathname === '/messages' || listAt === location.pathname ? 'list' : 'content';
  const setMobileView = (v: 'list' | 'content') => setListAt(v === 'list' ? location.pathname : null);

  const apply = useCallback((data: ShellData) => {
    setSpaces(data.spaces);
    setCapabilities(data.capabilities);
    setActiveId((cur) => (cur && data.spaces.some((s) => s.id === cur) ? cur : (data.spaces[0]?.id ?? null)));
  }, []);

  const reload = useCallback(async () => apply(await loadShellData()), [apply]);

  useEffect(() => {
    let cancelled = false;
    void loadShellData().then((d) => !cancelled && apply(d));
    return () => {
      cancelled = true;
    };
  }, [apply]);

  const activeSpace = spaces.find((s) => s.id === activeId) ?? null;

  useEffect(() => {
    if (!activeSpace) return;
    void api<ExperienceDto>('GET', `/spaces/${activeSpace.id}/experience`).then(setExperience);
  }, [activeSpace]);

  const nav = experience?.persona.navigation ?? ['messages', 'calls', 'spaces', 'discover', 'ai'];

  return (
    <ShellCtx.Provider value={{ spaces, activeSpace, experience, capabilities, selectSpace: setActiveId, reload, setMobileView }}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <div className="shell" data-mobile-view={mobileView}>
        <nav className="rail" aria-label="Main">
          {nav.map((item) => (
            <NavLink key={item} to={`/${item}`} className="rail-link">
              <Icon name={item} />
              <span>{t(NAV_LABELS[item])}</span>
            </NavLink>
          ))}
          <NavLink to="/settings" className="rail-link rail-bottom">
            <Icon name="settings" />
            <span>{t('nav.settings')}</span>
          </NavLink>
        </nav>

        <aside className="list-pane" aria-label={t('spaces.title')}>
          <div className="pane-header">
            <label className="visually-hidden" htmlFor="space-switcher">
              {t('shell.activeSpace')}
            </label>
            <select
              id="space-switcher"
              className="input space-switcher"
              value={activeId ?? ''}
              onChange={(e) => setActiveId(e.target.value)}
            >
              {spaces.map((s) => (
                <option key={s.id} value={s.id}>
                  {t(`spaces.type.${s.type}` as MessageKey)} · {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="stack" style={{ padding: 'var(--space-4)' }}>
            {me && !me.emailVerified && <EmailBanner email={me.email} />}
            {me?.ageBand === 'minor' && <div className="alert alert-info">{t('shell.minorNotice')}</div>}
            {me?.accountState === 'restricted' && <div className="alert alert-info">{t('shell.guardianPending')}</div>}
            <p className="muted">
              {t('spaces.persona')}:{' '}
              <strong>{experience ? (experience.persona.label[i18n.locale] ?? experience.persona.label.tr) : '…'}</strong>
            </p>
          </div>
          {config?.flags.messaging ? (
            <ConversationList />
          ) : (
            <div className="empty-state">
              <Icon name="messages" />
              <p>{t('module.disabled.title')}</p>
            </div>
          )}
        </aside>

        <main className="content-pane" id="main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      {!online && (
        <div className="connection-banner" role="status">
          {t('conn.offline')}
        </div>
      )}
    </ShellCtx.Provider>
  );
}

function EmailBanner({ email }: { email: string }) {
  const { i18n } = useApp();
  const [status, setStatus] = useState<string | null>(null);
  const resend = async () => {
    try {
      await api('POST', '/auth/email/resend');
      setStatus(i18n.t('banner.resent'));
    } catch (err) {
      setStatus(i18n.t(errorMessageKey(err)));
    }
  };
  return (
    <div className="alert alert-info stack" style={{ gap: 'var(--space-2)' }}>
      <span>{i18n.t('banner.emailUnverified', { email })}</span>
      {status ? (
        <span role="status">{status}</span>
      ) : (
        <button className="btn" type="button" onClick={() => void resend()}>
          {i18n.t('banner.resend')}
        </button>
      )}
    </div>
  );
}

/** Shown for modules that exist in the navigation but are not yet implemented (flag off). */
export function DisabledModule({ labelKey }: { labelKey: MessageKey }) {
  const { i18n } = useApp();
  const { setMobileView } = useShell();
  return (
    <div className="content-inner">
      <button className="btn mobile-only" type="button" onClick={() => setMobileView('list')}>
        <Icon name="back" label="Back" />
      </button>
      <div className="empty-state" role="status">
        <Icon name="lock" />
        <h2>{i18n.t('module.disabled.title')}</h2>
        <p>{i18n.t('module.disabled.body', { module: i18n.t(labelKey) })}</p>
      </div>
    </div>
  );
}
